import type { ReactNode } from 'react'
import { AlertCircle, Clock3, Loader2, PackageSearch, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Page title row: one h1 and the page's actions (demo `SectionHeader`). */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between', className)}>
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}

/** A bordered work panel with an optional title bar (demo `SurfaceCard`). */
export function SurfaceCard({
  title,
  actions,
  children,
  className,
  ...props
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
} & Omit<React.ComponentProps<'section'>, 'title'>) {
  return (
    <section className={cn('overflow-hidden rounded-xl border bg-card', className)} {...props}>
      {title ? (
        <div className="flex min-h-12 flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  )
}

type PageStateKind = 'pending' | 'empty' | 'error' | 'loading'

const stateIcons: Record<PageStateKind, LucideIcon> = {
  pending: Clock3,
  empty: PackageSearch,
  error: AlertCircle,
  loading: Loader2,
}

/** Centered loading, empty, pending or error message inside a panel or page. */
export function PageState({
  title,
  description,
  kind = 'pending',
  compact = false,
  action,
}: {
  title: string
  description?: string
  kind?: PageStateKind
  compact?: boolean
  action?: ReactNode
}) {
  const Icon = stateIcons[kind]
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-5 text-center',
        compact ? 'min-h-40 py-8' : 'min-h-80 py-12',
      )}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      <span
        className={cn(
          'mb-3 grid size-10 place-items-center rounded-lg border',
          kind === 'error' ? 'border-destructive/30 bg-destructive/5 text-destructive' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon aria-hidden="true" className={cn('size-5', kind === 'loading' && 'animate-spin motion-reduce:animate-none')} />
      </span>
      <h3 className="text-sm font-semibold">{title}</h3>
      {description ? <p className="mt-1 max-w-[52ch] text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return <PageState kind="loading" title={label} />
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <PageState action={action} description={description} kind="empty" title={title} />
}

export function ErrorState({
  title,
  description,
  onRetry,
}: {
  title: string
  description?: string
  onRetry?: () => void
}) {
  return (
    <PageState
      action={onRetry ? <Button onClick={onRetry} size="sm" variant="outline">Try again</Button> : undefined}
      description={description}
      kind="error"
      title={title}
    />
  )
}
